import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Visualizer from "@/components/Visualizer";
import {
  Lightning, WaveSine, Cpu, Stack, MusicNote, SpeakerHigh,
  Sparkle, Cube, Equalizer, ArrowRight, Play, GraphicsCard, Disc
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { playNote } from "@/lib/audio";

const SAMPLE_PROMPTS = [
  "dark rage melody",
  "playboi carti style synth",
  "space ambient pad",
  "detroit bell",
  "hard underground lead",
  "jersey club bass",
  "hyperpop bell",
  "drill 808 sub",
];

export default function Landing() {
  const [prompt, setPrompt] = useState("dark rage melody");
  const [generating, setGenerating] = useState(false);
  const [preset, setPreset] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1800);
    return () => clearInterval(t);
  }, []);

  const generate = async (p = prompt) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-preset-public", { prompt: p });
      setPreset(data.preset);
      // Auto-preview the preset
      setTimeout(() => playNote(data.preset, 60, 0.6), 80);
      setTimeout(() => playNote(data.preset, 63, 0.6), 280);
      setTimeout(() => playNote(data.preset, 67, 0.9), 540);
    } catch (e) {
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="absolute inset-0 scanlines opacity-50 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1765408217331-6d73ee0fc260?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "screen",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black pointer-events-none" />

        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-8">
                <span className="text-[10px] tracking-[0.4em] uppercase text-brand-red border border-brand-red/40 px-3 py-1.5 clip-tag" data-testid="hero-badge">
                  ▶ BUILD 1.0.4 — LIVE
                </span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim hidden sm:inline">
                  FOR FL STUDIO / VST3 / AU
                </span>
              </div>

              <h1 className="font-display font-black uppercase tracking-tighter leading-[0.86]" data-testid="hero-headline">
                <span className="block text-5xl sm:text-7xl lg:text-[110px]">
                  THE <span className="text-brand-red neon-red">808</span>
                </span>
                <span className="block text-5xl sm:text-7xl lg:text-[110px] chrome-text">FORGE</span>
                <span className="block text-4xl sm:text-6xl lg:text-[88px] -mt-2">
                  FOR <span className="text-brand-purple neon-purple">UNDER—</span>
                </span>
                <span className="block text-4xl sm:text-6xl lg:text-[88px] -mt-2">GROUND.</span>
              </h1>

              <p className="text-ink-mute text-sm sm:text-base mt-8 max-w-xl leading-relaxed">
                AI-driven wavetable synthesis, custom 808 design, and a producer ecosystem engineered
                for trap, rage, drill, hyperpop and the genres your DAW hasn&apos;t named yet.
                Type a vibe. Get a preset. Press a key.
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-10">
                <Link
                  to="/plugin"
                  data-testid="hero-cta-plugin"
                  className="bg-brand-red text-black text-xs font-bold tracking-[0.25em] uppercase px-6 py-3.5 hover:bg-white transition-colors btn-glitch clip-arrow-r inline-flex items-center gap-2"
                >
                  <Lightning size={16} weight="fill" /> See The Plugin
                </Link>
                <Link
                  to="/download"
                  data-testid="hero-cta-download"
                  className="bg-brand-purple text-black text-xs font-bold tracking-[0.25em] uppercase px-6 py-3.5 hover:bg-white transition-colors btn-glitch clip-arrow-r inline-flex items-center gap-2"
                >
                  ↓ Download VST
                </Link>
                <Link
                  to="/auth?mode=signup"
                  data-testid="hero-cta-start"
                  className="border border-zinc-700 text-white text-xs font-bold tracking-[0.25em] uppercase px-6 py-3.5 hover:border-brand-red hover:text-brand-red transition-colors inline-flex items-center gap-2"
                >
                  <Play size={14} weight="fill" /> Get Access
                </Link>
                <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-ink-dim ml-2">
                  <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse" />
                  12,847 producers shipping tonight
                </div>
              </div>

              {/* Genre chips */}
              <div className="mt-8 flex flex-wrap gap-2">
                {["TRAP", "RAGE", "DRILL", "HYPERPOP", "DETROIT", "JERSEY", "DARK AMBIENT", "EXPERIMENTAL"].map((g) => (
                  <span key={g} className="text-[10px] tracking-[0.25em] uppercase border border-zinc-800 text-ink-mute px-2.5 py-1.5">
                    {g}
                  </span>
                ))}
              </div>
            </div>

            {/* AI Prompt console */}
            <div className="lg:col-span-5">
              <div className="border border-zinc-800 bg-bg-surface relative grain" data-testid="hero-prompt-console">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-900 bg-black/40">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-brand-red rounded-full" />
                    <span className="w-2 h-2 bg-brand-orange rounded-full" />
                    <span className="w-2 h-2 bg-brand-green rounded-full" />
                    <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim ml-3">PROMPT://SYNTH-LAB</span>
                  </div>
                  <span className="text-[9px] tracking-[0.3em] uppercase text-brand-red animate-flicker">REC</span>
                </div>
                <div className="p-5">
                  <label className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">Describe a sound</label>
                  <div className="flex mt-2 border border-zinc-800 focus-within:border-brand-red transition-colors">
                    <span className="px-3 text-brand-red font-bold flex items-center">›</span>
                    <input
                      data-testid="hero-prompt-input"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && generate()}
                      placeholder="dark rage melody"
                      className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-ink-dim outline-none font-mono"
                    />
                    <button
                      onClick={() => generate()}
                      disabled={generating}
                      data-testid="hero-prompt-generate"
                      className="bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-4 hover:bg-white disabled:opacity-50 transition-colors"
                    >
                      {generating ? "..." : "GEN ▸"}
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {SAMPLE_PROMPTS.map((p) => (
                      <button
                        key={p}
                        onClick={() => { setPrompt(p); generate(p); }}
                        data-testid={`sample-prompt-${p.replace(/\s+/g, "-")}`}
                        className="text-[10px] tracking-[0.2em] uppercase text-ink-mute border border-zinc-800 px-2 py-1 hover:border-brand-red hover:text-brand-red transition-colors"
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  {/* Visualizer */}
                  <div className="mt-5 border border-zinc-900 bg-black">
                    <Visualizer height={120} style="wave" color={preset?.color_primary || "#FF003C"} color2={preset?.color_secondary || "#8B00FF"} />
                  </div>

                  {/* Preset readout */}
                  {preset && (
                    <div className="mt-4 border border-zinc-900 p-3 bg-black/60" data-testid="hero-preset-readout">
                      <div className="flex items-center justify-between">
                        <div className="font-display text-lg tracking-tighter">{preset.name}</div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">{preset.waveform}</div>
                      </div>
                      {/* AUTO KEY DETECTION */}
                      {preset.key && (
                        <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px] tracking-[0.25em] uppercase border-y border-zinc-900 py-2">
                          <span className="text-brand-purple flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 bg-brand-purple rounded-full animate-pulse" /> AUTO-KEY
                          </span>
                          <span className="text-white font-bold">{preset.key.toUpperCase()}</span>
                          <span className="text-ink-dim">·</span>
                          <span className="text-brand-red">{preset.bpm_suggestion} BPM</span>
                          <span className="text-ink-dim">·</span>
                          <span className="text-ink-mute font-mono">{preset.chord_progression?.join(" → ")}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2 mt-3 text-[10px] tracking-[0.15em] uppercase">
                        <ParamPill label="DRIVE" v={preset.drive} />
                        <ParamPill label="REVERB" v={preset.reverb} />
                        <ParamPill label="FILTER" v={preset.filter_cutoff} />
                        <ParamPill label="BITCRUSH" v={preset.bitcrush} />
                        <ParamPill label="DELAY" v={preset.delay} />
                        <ParamPill label="WIDTH" v={preset.stereo_width} />
                      </div>
                      <div className="mt-3 flex gap-1.5 flex-wrap">
                        {preset.tags.slice(0, 5).map((t) => (
                          <span key={t} className="text-[9px] tracking-[0.2em] uppercase border border-zinc-800 px-1.5 py-0.5 text-ink-mute">#{t}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between text-[9px] tracking-[0.3em] uppercase text-ink-dim">
                    <span>↑ TRY ANY VIBE — INSTANT PRESET</span>
                    <span className="animate-flicker">● LATENCY 0.04s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom marquee */}
          <div className="mt-16 border-y border-zinc-900 py-4 overflow-hidden">
            <div className="flex items-center gap-12 whitespace-nowrap animate-marqueeX font-display text-2xl tracking-tight">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center gap-12 pr-12">
                  <span>⟶ WAVETABLE SYNTHESIS</span>
                  <span className="text-brand-red">●</span>
                  <span>AI PROMPT → PRESET</span>
                  <span className="text-brand-purple">●</span>
                  <span>808 DESIGNER</span>
                  <span className="text-brand-red">●</span>
                  <span>FL STUDIO NATIVE</span>
                  <span className="text-brand-purple">●</span>
                  <span>VST3 / AU</span>
                  <span className="text-brand-red">●</span>
                  <span>MARKETPLACE</span>
                  <span className="text-brand-purple">●</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <Features />

      {/* SECONDARY VISUAL BLOCK */}
      <ProducerBlock />

      {/* SHOWCASE */}
      <Showcase />

      {/* CTA */}
      <FinalCta />
    </div>
  );
}

function ParamPill({ label, v }) {
  return (
    <div className="border border-zinc-900 px-2 py-1.5 flex items-center justify-between bg-black/40">
      <span className="text-ink-dim">{label}</span>
      <span className="text-white font-mono">{typeof v === "number" ? v.toFixed(2) : v}</span>
    </div>
  );
}

function Features() {
  const items = [
    { Icon: WaveSine, title: "WAVETABLE SYNTH", text: "Multi-layer oscillators, drag-edit waveforms, harmonic morph, analog warmth emulation, ultra-clean interpolation.", color: "text-brand-red" },
    { Icon: Sparkle, title: "AI PROMPT → PRESET", text: "Type a vibe. Get a sound. The engine learns your habits and adapts to your sonic identity over time.", color: "text-brand-purple" },
    { Icon: SpeakerHigh, title: "808 DESIGNER", text: "Glide editor, dynamic distortion modeling, soft clipper, saturation, sub enhancer, one-click punch.", color: "text-brand-red" },
    { Icon: Equalizer, title: "EFFECTS RACK", text: "Reverb, delay, granular FX, multiband distortion, tape emulation, AI auto-mixer, parallel processing.", color: "text-brand-purple" },
    { Icon: MusicNote, title: "CREATIVE MIDI TOOLS", text: "Chord generator, scale lock, melody randomizer, arpeggiator, counter melodies, auto harmony.", color: "text-brand-red" },
    { Icon: Cpu, title: "FL STUDIO NATIVE", text: "Low-latency playback, multi-core processing, drag-and-drop, automation clip support, Win + macOS.", color: "text-brand-purple" },
  ];
  return (
    <section className="relative py-24 lg:py-32 border-b border-zinc-900" data-testid="features-section">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-3">◢ THE STACK</div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
              EVERYTHING<br />A PRODUCER<br />NEEDS. <span className="text-brand-red">NOTHING</span> THEY DON&apos;T.
            </h2>
          </div>
          <p className="max-w-md text-sm text-ink-mute leading-relaxed">
            Six modules. One workflow. Built around how modern trap and underground producers actually
            move from idea to finished record — fast, loud, and yours.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
          {items.map(({ Icon, title, text, color }, i) => (
            <div key={i} className="group bg-bg-surface p-8 hover:bg-bg-surface2 transition-colors relative" data-testid={`feature-card-${i}`}>
              <div className={`${color} mb-6 inline-flex w-12 h-12 border border-current items-center justify-center`}>
                <Icon size={22} weight="bold" />
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-2">MODULE_0{i + 1}</div>
              <h3 className="font-display font-bold text-xl tracking-tight uppercase mb-3">{title}</h3>
              <p className="text-xs text-ink-mute leading-relaxed">{text}</p>
              <div className="absolute top-0 right-0 w-8 h-8 border-r border-t border-brand-red opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-l border-b border-brand-red opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProducerBlock() {
  return (
    <section className="relative py-24 lg:py-32 border-b border-zinc-900 overflow-hidden">
      <div className="absolute inset-0 opacity-25"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1654198032178-a0d7506cb176?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-4">◢ PRODUCER ECOSYSTEM</div>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
            FROM <span className="text-brand-red neon-red">IDEA</span><br />
            TO <span className="chrome-text">RECEIPT</span><br />
            IN ONE PLUGIN.
          </h2>
          <p className="text-ink-mute text-sm mt-8 max-w-lg leading-relaxed">
            BEAT-LAB isn&apos;t a synth — it&apos;s the platform. Sell your presets in the marketplace,
            collab live with other producers, render with priority servers, and access drop-day artist packs
            before they hit anywhere else.
          </p>

          <div className="grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 mt-10 max-w-xl">
            {[
              ["12.8K", "Producers"],
              ["340K+", "Presets shipped"],
              ["$2.1M", "Paid to creators"],
            ].map(([n, l]) => (
              <div key={l} className="bg-bg-surface p-5">
                <div className="font-display text-3xl font-black tracking-tighter text-brand-red">{n}</div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:pl-10">
          <div className="border border-zinc-800 bg-black/60 grain p-6">
            <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-4">
              <span>SIGNAL FLOW</span>
              <span className="text-brand-red">● LIVE</span>
            </div>
            <Visualizer height={220} style="bars" />
            <div className="mt-4 grid grid-cols-4 gap-px bg-zinc-900">
              {["OSC", "FILTER", "DRIVE", "FX"].map((s, i) => (
                <div key={s} className="bg-bg-surface px-3 py-2 text-center">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">CH_0{i + 1}</div>
                  <div className="font-display text-sm mt-1">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  const showcase = [
    { title: "AI VOCAL TEXTURES", sub: "PRO TIER", icon: Sparkle },
    { title: "MARKETPLACE", sub: "SELL YOUR SOUNDS", icon: Stack },
    { title: "PRIORITY RENDER", sub: "DROP-DAY SHIPPING", icon: GraphicsCard },
    { title: "SIGNATURE PACKS", sub: "MONTHLY DROPS", icon: Disc },
  ];
  return (
    <section className="py-24 lg:py-32 border-b border-zinc-900">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
        <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-3">◢ INCLUDED IN THE LAB</div>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-12">
          MORE THAN A PLUGIN. <br />A <span className="text-brand-purple neon-purple">CREATIVE</span> OS.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
          {showcase.map(({ title, sub, icon: Icon }, i) => (
            <div key={title} className="bg-bg-surface p-8 group hover:bg-bg-surface2 transition-colors">
              <Icon size={28} weight="bold" className="text-brand-red mb-6" />
              <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mb-1">{sub}</div>
              <h3 className="font-display font-bold uppercase tracking-tight text-xl">{title}</h3>
              <div className="mt-6 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-ink-mute group-hover:text-brand-red transition-colors">
                <span>EXPLORE</span>
                <ArrowRight size={14} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 scanlines opacity-30 pointer-events-none" />
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-4">◢ ENTER THE LAB</div>
        <h2 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[120px] leading-[0.85]">
          MAKE IT<br />
          <span className="text-brand-red neon-red">LOUDER.</span>
        </h2>
        <p className="text-ink-mute text-sm mt-8 max-w-lg mx-auto leading-relaxed">
          Three plans. One platform. Start at $14.99 — no demo limits, no watermarks, no cap on what you can ship.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link to="/pricing" data-testid="footer-cta-pricing" className="bg-brand-red text-black text-xs font-bold tracking-[0.25em] uppercase px-7 py-4 hover:bg-white transition-colors btn-glitch clip-arrow-r">
            VIEW PRICING ▸
          </Link>
          <Link to="/playground" data-testid="footer-cta-demo" className="border border-zinc-700 text-white text-xs font-bold tracking-[0.25em] uppercase px-7 py-4 hover:border-brand-purple hover:text-brand-purple transition-colors">
            TRY THE SYNTH ▸
          </Link>
        </div>
      </div>
    </section>
  );
}
